/**
 * Fatura şart kuralları — örn. giriş tutarına göre tamir / 24 ayar gram.
 */

export const INVOICE_CONDITION_SCOPES = [
  { value: 'giris', label: 'Giriş / döviz' },
  { value: 'satis', label: 'Satış' }
];

/** Varsayılan kapsam — eski kayıtlarda scope alanı yoksa bu kabul edilir. */
export const DEFAULT_CONDITION_SCOPE = 'giris';

export const INVOICE_CONDITION_WHEN = [
  { value: 'amount_lt', label: 'Bu tutarın altında' },
  { value: 'amount_lte', label: 'Bu tutar ve altında' },
  { value: 'amount_gte', label: 'Bu tutar ve üzerinde' },
  { value: 'amount_gt', label: 'Bu tutarın üzerinde' }
];

export const INVOICE_CONDITION_THEN = [
  { value: 'tamir', label: 'Altın Tamir Ücreti olarak kesilsin' },
  { value: '24gram', label: '24 Ayar Gram Altın olarak kesilsin' },
  { value: '22gram', label: '22 Ayar Gram Altın olarak kesilsin' },
  { value: '22taki', label: '22 Ayar Takı olarak kesilsin' },
  { value: '22bilezik', label: '22 Ayar Bilezik olarak kesilsin' },
  {
    value: 'enYakinUrun',
    label: 'Tutara en yakın ürün olarak kesilsin (gram / çeyrek / yarım / tam)'
  }
];

const newId = () => `rule-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

export const createInvoiceConditionRule = (partial = {}) => ({
  id: partial.id || newId(),
  enabled: partial.enabled !== false,
  scope: partial.scope || DEFAULT_CONDITION_SCOPE,
  when: partial.when || 'amount_lt',
  amountTl: Number(partial.amountTl) >= 0 ? Number(partial.amountTl) : 3000,
  thenInvoiceAs: partial.thenInvoiceAs || 'tamir',
  /**
   * Yalnızca satışın bir kısmı bankadan geldiyse çalışsın.
   * Kapalıyken (varsayılan) kural her ödemede geçerlidir — eski kurallar aynen
   * çalışmaya devam eder.
   */
  onlyPartialPayment: partial.onlyPartialPayment === true
});

export const DEFAULT_INVOICE_CONDITION_RULES = [
  createInvoiceConditionRule({
    id: 'giris-tamir-default',
    scope: 'giris',
    when: 'amount_lt',
    amountTl: 3000,
    thenInvoiceAs: 'tamir'
  }),
  createInvoiceConditionRule({
    id: 'giris-24gram-default',
    scope: 'giris',
    when: 'amount_gte',
    amountTl: 3000,
    thenInvoiceAs: '24gram'
  })
];

/** Eski girisTamirMaxTl / girisGramAltinMinTl alanlarından kural listesi üretir. */
export const conditionRulesFromLegacyThresholds = (settings = {}) => {
  const tamirMax = Number(settings?.girisTamirMaxTl);
  const gramMin = Number(settings?.girisGramAltinMinTl);
  return [
    createInvoiceConditionRule({
      id: 'giris-tamir-default',
      scope: 'giris',
      when: 'amount_lt',
      amountTl: Number.isFinite(tamirMax) && tamirMax >= 0 ? tamirMax : 3000,
      thenInvoiceAs: 'tamir'
    }),
    createInvoiceConditionRule({
      id: 'giris-24gram-default',
      scope: 'giris',
      when: 'amount_gte',
      amountTl: Number.isFinite(gramMin) && gramMin >= 0 ? gramMin : 3000,
      thenInvoiceAs: '24gram'
    })
  ];
};

export const mergeInvoiceConditionRules = (rows, legacySettings = null) => {
  if (Array.isArray(rows) && rows.length > 0) {
    return rows.map((row) => createInvoiceConditionRule(row)).filter((r) => r.scope && r.when && r.thenInvoiceAs);
  }
  if (legacySettings && (legacySettings.girisTamirMaxTl != null || legacySettings.girisGramAltinMinTl != null)) {
    return conditionRulesFromLegacyThresholds(legacySettings);
  }
  return DEFAULT_INVOICE_CONDITION_RULES.map((r) => ({ ...r }));
};

/** Kayıt uyumu için eski alanları kurallardan türet. */
export const deriveLegacyGirisThresholds = (rules = []) => {
  const list = mergeInvoiceConditionRules(rules);
  const tamir = list.find((r) => r.scope === 'giris' && r.thenInvoiceAs === 'tamir' && /amount_lt/.test(r.when));
  const gram = list.find((r) => r.scope === 'giris' && r.thenInvoiceAs === '24gram' && /amount_gt/.test(r.when));
  return {
    girisTamirMaxTl: Number(tamir?.amountTl) >= 0 ? Number(tamir.amountTl) : 3000,
    girisGramAltinMinTl: Number(gram?.amountTl) >= 0 ? Number(gram.amountTl) : 3000
  };
};

export const matchConditionWhen = (when, amount, threshold) => {
  const a = Number(amount) || 0;
  const t = Number(threshold) || 0;
  switch (when) {
    case 'amount_lt':
      return a < t;
    case 'amount_lte':
      return a <= t;
    case 'amount_gte':
      return a >= t;
    case 'amount_gt':
      return a > t;
    default:
      return false;
  }
};

/**
 * İlk eşleşen kuralın thenInvoiceAs değerini döner (veya null).
 * SIRA ÖNEMLİDİR: listede önce gelen kural kazanır, sonrakiler denenmez.
 * @param {'giris'|'satis'} scope
 * @param {{ partialPayment?: boolean }} [context] - "yalnızca kısmi ödeme" kutusu
 *   işaretli kurallar bu bilgiye bakar.
 */
export const resolveInvoiceConditionAction = (settings, scope, amount, context = null) => {
  const rules = mergeInvoiceConditionRules(settings?.invoiceConditionRules, settings).filter(
    (r) => r.enabled !== false && r.scope === scope
  );
  for (const rule of rules) {
    if (rule.onlyPartialPayment && !context?.partialPayment) continue;
    if (matchConditionWhen(rule.when, amount, rule.amountTl)) {
      return rule.thenInvoiceAs;
    }
  }
  return null;
};
